import React, { useState, useMemo } from 'react';
import CalculatorLayout from '../../components/CalculatorLayout';
import SliderInput from '../../components/SliderInput';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const LogCalculator = () => {
  const [x, setX] = useState(1000);
  const [base, setBase] = useState(10);

  const result = useMemo(() => {
    if (x <= 0 || base <= 0 || base === 1) return NaN;
    return Math.log(x) / Math.log(base);
  }, [x, base]);

  return (
    <CalculatorLayout
      title="Log Calculator"
      subtitle="Calculate logarithms of any base, including log base 10 and natural log (ln)."
      category="Math"
      description={<p>A logarithm answers the question: <em>to what power must the base be raised to get the number?</em> For example, log₁₀(1000) = 3 because 10³ = 1000. Natural log (ln) uses Euler's number (e ≈ 2.71828) as the base.</p>}
      formulaNote="logₙ(x) = ln(x) / ln(n)   •  log₁₀(1000) = 3   •  ln(e) = 1"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-5">
          <div>
            <Label className="text-sm font-medium text-slate-700 mb-2 block">Value (x)</Label>
            <Input type="number" value={x} step="0.0001" onChange={(e) => setX(parseFloat(e.target.value) || 0)} className="text-lg font-semibold h-11" />
          </div>
          <SliderInput label="Base (n)" value={base} onChange={setBase} min={2} max={100} step={1} />
          <div className="flex gap-2 flex-wrap">
            {[
              { l: 'log₁₀ (common)', v: 10 },
              { l: 'ln (natural)', v: Math.E },
              { l: 'log₂ (binary)', v: 2 },
            ].map(p => (
              <button key={p.l} onClick={() => setBase(p.v)} className="px-3 py-1.5 rounded-md text-xs border border-slate-200 hover:bg-saffron-50 hover:border-saffron-200">{p.l}</button>
            ))}
          </div>
        </div>

        <div className="bg-gradient-to-br from-saffron-50 to-indgreen-50/40 rounded-xl border border-slate-200 p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Result</div>
          <div className="font-display text-lg text-bharat-navy mt-1">log<sub>{base === Math.E ? 'e' : base.toFixed(0)}</sub>({x}) =</div>
          <div className="font-display text-3xl md:text-4xl font-bold text-saffron-700 display-font mt-1">
            {isNaN(result) ? 'Undefined' : result.toFixed(8).replace(/\.?0+$/, '')}
          </div>
          <div className="mt-5 space-y-2 text-sm">
            <Row label="log₁₀(x)" value={x > 0 ? Math.log10(x).toFixed(6) : 'N/A'} />
            <Row label="ln(x)" value={x > 0 ? Math.log(x).toFixed(6) : 'N/A'} />
            <Row label="log₂(x)" value={x > 0 ? Math.log2(x).toFixed(6) : 'N/A'} />
          </div>
        </div>
      </div>
    </CalculatorLayout>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
    <span className="text-slate-600">{label}</span>
    <span className="display-font font-semibold text-bharat-navy">{value}</span>
  </div>
);

export default LogCalculator;
